import { useSyncExternalStore } from 'react';

/**
 * A tab opened before a deploy keeps running the previous version until it is reloaded. The
 * console reloads by itself only when a page it needs is gone (`staleChunk.ts`), which can happen
 * in the middle of a form. This module notices the new version earlier, while nothing is lost
 * yet, so the shell can offer the reload and the person chooses when (`NewVersionBar`).
 */

/**
 * The file that names the published version, `{"buildId":"<id>"}`: the build writes it next to
 * `index.html` (`versionFilePlugin` in `vite.config.ts`), and the edge serves it with `no-store`
 * like the rest of the SPA (`deploy/Caddyfile`).
 */
export const VERSION_FILE = 'version.json';

/**
 * The element of `index.html` that names the version this tab loaded, `<meta name="sl-build-id"
 * content="<id>">`, written by the same build with the same id. It lives in the document, not in
 * the bundle, so that the id never changes the name of a chunk (`vite.config.ts`).
 */
export const BUILD_ID_META = 'sl-build-id';

/** How often an open tab asks which version is published. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * How long a request for the version file may take. One that never answers (a laptop that went to
 * sleep, a phone that changed networks) is abandoned, so it does not hold back every check after
 * it: only one is in flight at a time.
 */
export const VERSION_REQUEST_TIMEOUT_MS = 10_000;

/** The id of the version a document was loaded with, or `null` when it carries none. */
export function readRunningBuildId(doc: Pick<Document, 'querySelector'>): string | null {
  const value = doc.querySelector(`meta[name="${BUILD_ID_META}"]`)?.getAttribute('content') ?? '';
  return value === '' ? null : value;
}

const loadedBuildId = typeof document === 'undefined' ? null : readRunningBuildId(document);

/**
 * The version this tab is running and whether it asks at all: only a production build that names
 * its version does. `pnpm dev` and the tests have no `version.json` and their `index.html` carries
 * no id. Behind an object so a test can turn the check on.
 */
export const runningVersion = {
  buildId: loadedBuildId ?? '',
  checks: import.meta.env.PROD && loadedBuildId !== null,
};

/**
 * The id a parsed version file carries, or `null` when the body is not one (an edge that answers
 * with something else, such as the SPA's `index.html` of an image older than this file).
 */
export function readVersionFile(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const buildId = (body as { buildId?: unknown }).buildId;
  return typeof buildId === 'string' && buildId.length > 0 ? buildId : null;
}

/**
 * The id of the published version, or `null` when it cannot be told: no connection, no answer
 * within `VERSION_REQUEST_TIMEOUT_MS`, an answer other than 200, or a body that is not the version
 * file. None of those says anything about a new version, so none of them is reported.
 */
export async function fetchPublishedBuildId(): Promise<string | null> {
  const abandon = new AbortController();
  const timer = setTimeout(() => {
    abandon.abort();
  }, VERSION_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`/${VERSION_FILE}`, {
      cache: 'no-store',
      signal: abandon.signal,
    });
    if (response.status !== 200) {
      return null;
    }
    return readVersionFile(await response.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export interface VersionWatchOptions {
  /** The id of the bundle this tab is running. */
  runningBuildId: string;
  /** Called once, when the published id is another one; the watch stops there. */
  onNewVersion: () => void;
  fetchPublished?: () => Promise<string | null>;
  intervalMs?: number;
}

/**
 * Asks for the published version every `intervalMs` and whenever the tab becomes visible again or
 * the window gets the focus back, never on start (the tab just loaded the published version). A
 * hidden tab skips its turn: it asks again as soon as it is shown. Only one request is in flight
 * at a time. Returns the function that stops the watch.
 */
export function watchPublishedVersion({
  runningBuildId,
  onNewVersion,
  fetchPublished = fetchPublishedBuildId,
  intervalMs = VERSION_CHECK_INTERVAL_MS,
}: VersionWatchOptions): () => void {
  let stopped = false;
  let inFlight = false;

  const check = async () => {
    if (stopped || inFlight) {
      return;
    }
    inFlight = true;
    try {
      const published = await fetchPublished().catch(() => null);
      if (!stopped && published !== null && published !== runningBuildId) {
        stop();
        onNewVersion();
      }
    } finally {
      inFlight = false;
    }
  };

  const checkIfVisible = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };
  const checkOnFocus = () => {
    void check();
  };

  const timer = setInterval(checkIfVisible, intervalMs);
  document.addEventListener('visibilitychange', checkIfVisible);
  window.addEventListener('focus', checkOnFocus);

  function stop() {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', checkIfVisible);
    window.removeEventListener('focus', checkOnFocus);
  }

  return stop;
}

/**
 * What every shell reads: whether another version was published since this tab loaded. It lives
 * outside React so that moving between the console and the portal (two shells) keeps the notice,
 * and one watch serves whichever shell is mounted.
 */
const notice = {
  found: false,
  listeners: new Set<() => void>(),
  stop: undefined as (() => void) | undefined,
};

function subscribe(listener: () => void): () => void {
  notice.listeners.add(listener);
  if (notice.stop === undefined && !notice.found && runningVersion.checks) {
    notice.stop = watchPublishedVersion({
      runningBuildId: runningVersion.buildId,
      onNewVersion: () => {
        notice.found = true;
        notice.stop = undefined;
        for (const each of notice.listeners) {
          each();
        }
      },
    });
  }
  return () => {
    notice.listeners.delete(listener);
    if (notice.listeners.size === 0) {
      notice.stop?.();
      notice.stop = undefined;
    }
  };
}

/** Whether another version was published since this tab loaded. */
export function useNewVersionAvailable(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => notice.found,
    () => false,
  );
}

/**
 * Forgets a version found earlier, as a freshly loaded tab would. A tab never needs it (the only
 * way out of the notice is the reload); tests do, since they mount the shells many times over in
 * one module.
 */
export function resetVersionNotice(): void {
  notice.stop?.();
  notice.stop = undefined;
  notice.found = false;
}
